'use client'

/**
 * Danger-toned banner shown across the (app) layout when the signed-in
 * user's `profiles.subscription_status` is `past_due`. The CTA opens a
 * fresh Stripe Customer Portal session via /api/v1/billing/portal so
 * the user can resolve billing in one click.
 *
 * Stripe webhooks flip `subscription_status` back to `active` once the
 * payment recovers (see src/app/api/webhooks/stripe/route.ts), which
 * unmounts this banner on the next layout render.
 */

import { useState } from 'react'

export function PastDueBanner() {
  const [opening, setOpening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const openPortal = async () => {
    setError(null)
    setOpening(true)
    try {
      const res = await fetch('/api/v1/billing/portal', { method: 'POST' })
      const body = (await res.json().catch(() => ({}))) as {
        data?: { url?: string }
        error?: string
      }
      if (res.ok && body.data?.url) {
        window.location.assign(body.data.url)
        return
      }
      setError(body.error ?? `Couldn't open the billing portal (HTTP ${res.status})`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error')
    } finally {
      setOpening(false)
    }
  }

  return (
    <div
      role="alert"
      className="w-full"
      style={{
        background: 'var(--color-semantic-error)',
        color: 'white',
      }}
    >
      <div className="max-w-6xl mx-auto py-2 px-4 flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="min-w-0">
          <p>
            <strong>Your payment failed.</strong> Resolve billing to keep Pro features active.
          </p>
          {error && (
            <p className="mt-0.5 text-xs" style={{ opacity: 0.9 }}>
              {error}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openPortal}
            disabled={opening}
            className="rounded-md px-3 py-1 text-sm font-medium"
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
            }}
          >
            {opening ? 'Opening…' : 'Update payment'}
          </button>
        </div>
      </div>
    </div>
  )
}
